import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UsersIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { cn } from '@/utils/cn';
import UserList from '@/components/settings/UserList';

type SettingsTab = 'users' | 'system';

interface Tab {
  id: SettingsTab;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const tabs: Tab[] = [
  {
    id: 'users',
    label: 'User Management',
    icon: UsersIcon,
  },
  {
    id: 'system',
    label: 'System Settings',
    icon: Cog6ToothIcon,
  },
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('users');

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          Settings
        </h1>
        <p className="text-secondary-600 dark:text-secondary-400 mt-1">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Navigation Tabs */}
      <Card>
        <CardContent className="p-2">
          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    isActive
                      ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-100'
                      : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:text-secondary-100 dark:hover:bg-secondary-800'
                  )}
                >
                  <Icon className={cn(
                    'w-4 h-4 mr-2',
                    isActive
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-secondary-500 dark:text-secondary-400'
                  )} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'users' && <UserList />}

          {activeTab === 'system' && (
            <Card>
              <CardHeader>
                <CardTitle>System Settings</CardTitle>
                <CardDescription>
                  Configure system-wide preferences and integrations
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-center py-12">
                  <Cog6ToothIcon className="w-16 h-16 mx-auto text-secondary-400 dark:text-secondary-600 mb-4" />
                  <p className="text-secondary-600 dark:text-secondary-400">
                    System settings coming soon
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
